import React, { useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { QueryProvider, useDataModel } from '@thd-nucleus/data-sources';
import { FitCompatibilityProvider, FitCompatibilityWidget } from './components';
import { FitDrawer } from './components/subcomponents';
import { REFRIGERATOR_ANIMATIONS, REFRIGERATOR_IMAGES } from './applianceConstants/refrigerator';
import { LAUNDRY_ANIMATIONS, LAUNDRY_IMAGES } from './applianceConstants/laundry';
import { RANGE_IMAGES, RANGE_ANIMATIONS } from './applianceConstants/range';
import {
  TYPE_LIST,
  VALID_APPLIANCE_TYPES
} from './constants';
import { isBuiltInAppliance, getApplianceType } from './utils';

const { oneOf, bool, string, shape } = proptypes;

export const FitCompatibility = ({
  navParam = '',
  itemId = '',
  type = '',
  drawerPosition = 'right',
  imageCacheEnabled = false,
  data: searchPropData = undefined
}) => {
  const [isLoading, setIsLoading] = useState(imageCacheEnabled && typeof window !== 'undefined');
  const [isValidApplianceType, setIsValidApplianceType] = useState(false);
  const isSearchPropValid = typeof searchPropData !== 'undefined';

  const { data: productData, loading: productLoading, error: productError } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: type !== 'PIP'
  });

  const { data: searchData, loading: searchLoading, error: searchError } = useDataModel('searchModel', {
    variables: {
      navParam,
    },
    skip: type === 'PIP' || isSearchPropValid
  });

  let activeData;

  if (type === 'PIP') {
    activeData = productData;
  } else if (isSearchPropValid) {
    activeData = searchPropData;
  } else {
    activeData = searchData;
  }

  const applianceType = getApplianceType(activeData) || '';
  const hideWidgetFitForBuiltIn = type === 'PIP' && isBuiltInAppliance(activeData);

  useEffect(() => {
    if (!productLoading && !searchLoading && activeData) {
      setIsValidApplianceType(VALID_APPLIANCE_TYPES.includes(applianceType));
    }
  }, [activeData]);

  /* istanbul ignore next */
  useEffect(() => {
    const cacheImages = async () => {
      if (typeof window === 'undefined') return;

      try {
        if (!TYPE_LIST.includes(type)) throw new Error('No type provided to FitCompatibility');
        await Promise.all([
          ...Object.values(REFRIGERATOR_ANIMATIONS),
          ...Object.values(REFRIGERATOR_IMAGES),
          ...Object.values(LAUNDRY_ANIMATIONS),
          ...Object.values(LAUNDRY_IMAGES),
          ...Object.values(RANGE_ANIMATIONS),
          ...Object.values(RANGE_IMAGES)
        ].map(async (src) => {
          return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = src;
            image.onload = resolve;
            image.onerror = reject;
          });
        }));
        setIsLoading(false);
      } catch (er) {
        console.log(er);
        setIsLoading(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, [imageCacheEnabled, type]);

  if (!TYPE_LIST.includes(type) || !isValidApplianceType || hideWidgetFitForBuiltIn) return null;
  
  return (
      <div
        data-testid="fit-compatibility"
        data-component="FitCompatibility"
      >
        <QueryProvider cacheKey="fit-compatibility">
          <FitCompatibilityProvider
            isLoading={isLoading}
            type={type}
            itemId={itemId}
            navParam={navParam}
            drawerPosition={drawerPosition}
            applianceType={applianceType}
          >
            <FitDrawer />
            <FitCompatibilityWidget />
          </FitCompatibilityProvider>
        </QueryProvider>
      </div>
  );
};

FitCompatibility.propTypes = {
  type: oneOf(TYPE_LIST),
  itemId: string,
  navParam: string,
  drawerPosition: oneOf(['left', 'right']),
  imageCacheEnabled: bool,
  data: shape({})
};

FitCompatibility.dataModel = FitCompatibilityProvider.dataModel;

FitCompatibility.displayName = 'FitCompatibility';
